import React, { useState, useCallback, useEffect } from 'react';
import { Button, Card } from '@/components/ui';
import { cn } from '@/lib/utils';
import { Check, ClipboardCheck, Minus, Plus, StickyNote, Loader2 } from 'lucide-react';
import type { StockLevel, InventoryCheckItem } from '@/types/models';

interface CheckFlowProps {
  checkId: number;
  stockLevels: StockLevel[];
  checkedItems: InventoryCheckItem[];
  onRecordItem: (data: { product_id: number; actual_quantity: number; notes?: string }) => Promise<void>;
  onComplete: () => Promise<void>;
  isRecording?: boolean;
  isCompleting?: boolean;
}

/* ── Inline QuantityStepper ─────────────────────────────────── */

interface QuantityStepperProps {
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}

const QuantityStepper: React.FC<QuantityStepperProps> = ({ value, onChange, disabled }) => (
  <div className="inline-flex items-center gap-1">
    <button
      type="button"
      disabled={disabled || value <= 0}
      onClick={() => onChange(Math.max(0, value - 1))}
      className={cn(
        'flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 bg-white',
        'text-gray-600 transition-all duration-150',
        'hover:bg-gray-50 hover:border-gray-300 active:scale-95',
        'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200',
      )}
      aria-label="Уменьшить"
    >
      <Minus className="w-3.5 h-3.5" />
    </button>

    <input
      type="number"
      min={0}
      value={value}
      disabled={disabled}
      onChange={(e) => {
        const n = parseInt(e.target.value, 10);
        onChange(Number.isNaN(n) ? 0 : Math.max(0, n));
      }}
      className={cn(
        'w-16 h-9 text-center text-sm font-semibold rounded-lg border border-gray-200 bg-white',
        'focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400',
        'disabled:bg-gray-50 disabled:cursor-not-allowed',
        '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
      )}
    />

    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(value + 1)}
      className={cn(
        'flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 bg-white',
        'text-gray-600 transition-all duration-150',
        'hover:bg-gray-50 hover:border-gray-300 active:scale-95',
        'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200',
      )}
      aria-label="Увеличить"
    >
      <Plus className="w-3.5 h-3.5" />
    </button>
  </div>
);

/* ── Discrepancy badge (for unchecked items live preview) ──── */

const DiscrepancyBadge: React.FC<{ diff: number }> = ({ diff }) => {
  if (diff === 0) return null;
  return (
    <span
      className={cn(
        'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold tabular-nums',
        diff < 0 ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600',
      )}
    >
      {diff > 0 ? '+' : ''}
      {diff}
    </span>
  );
};

/* ── Main CheckFlow component ──────────────────────────────── */

const CheckFlow: React.FC<CheckFlowProps> = ({
  stockLevels,
  checkedItems,
  onRecordItem,
  onComplete,
  isRecording,
  isCompleting,
}) => {
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [expandedNotes, setExpandedNotes] = useState<Record<number, boolean>>({});
  const [recordingId, setRecordingId] = useState<number | null>(null);

  // Only consider items with actual_quantity recorded as "checked"
  // Backend returns ALL items (including unrecorded ones with actual_quantity=null)
  const actuallyChecked = checkedItems.filter((ci) => ci.actual_quantity !== null && ci.actual_quantity !== undefined);
  const checkedProductIds = new Set(actuallyChecked.map((ci) => ci.product_id));

  // Initialize quantities from stockLevels (expected values) for unchecked items
  useEffect(() => {
    const init: Record<number, number> = {};
    let changed = false;
    for (const sl of stockLevels) {
      if (!checkedProductIds.has(sl.product_id) && quantities[sl.product_id] === undefined) {
        init[sl.product_id] = sl.quantity;
        changed = true;
      }
    }
    if (changed) {
      setQuantities((prev) => ({ ...prev, ...init }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockLevels, checkedItems]);

  const setQuantity = useCallback((productId: number, val: number) => {
    setQuantities((prev) => ({ ...prev, [productId]: val }));
  }, []);

  const setNote = useCallback((productId: number, val: string) => {
    setNotes((prev) => ({ ...prev, [productId]: val }));
  }, []);

  const toggleNotes = useCallback((productId: number) => {
    setExpandedNotes((prev) => ({ ...prev, [productId]: !prev[productId] }));
  }, []);

  const handleRecordItem = useCallback(
    async (productId: number) => {
      setRecordingId(productId);
      try {
        await onRecordItem({
          product_id: productId,
          actual_quantity: quantities[productId] ?? 0,
          notes: notes[productId] || undefined,
        });
      } finally {
        setRecordingId(null);
      }
    },
    [onRecordItem, quantities, notes],
  );

  const progress = stockLevels.length > 0 ? (actuallyChecked.length / stockLevels.length) * 100 : 0;
  const allChecked = stockLevels.length > 0 && stockLevels.every((sl) => checkedProductIds.has(sl.product_id));

  return (
    <div className="space-y-5">
      {/* ── Progress bar ─────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">
            Проверено: <span className="font-semibold">{actuallyChecked.length}</span> из{' '}
            <span className="font-semibold">{stockLevels.length}</span>
          </span>
          <span className="text-gray-400 text-xs">{Math.round(progress)}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-500 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* ── Product list ─────────────────────────────────────── */}
      <div className="space-y-2">
        {stockLevels.map((sl) => {
          const checked = actuallyChecked.find((ci) => ci.product_id === sl.product_id);
          const isItemRecording = isRecording && recordingId === sl.product_id;
          const qty = quantities[sl.product_id] ?? sl.quantity;
          const diff = qty - sl.quantity;
          const noteExpanded = expandedNotes[sl.product_id] ?? false;

          return (
            <Card
              key={sl.product_id}
              padding="sm"
              className={cn(
                'transition-all duration-200',
                checked && 'bg-emerald-50/40 border-emerald-100',
              )}
            >
              {checked ? (
                /* ── Checked product row ──────────────────── */
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-emerald-100">
                    <Check className="w-4 h-4 text-emerald-600" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{sl.name}</p>
                    <p className="text-xs text-gray-500">{sl.sku}</p>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-gray-400">Ожид: {sl.quantity}</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {checked.actual_quantity ?? '—'}
                    </span>
                    {checked.discrepancy !== null && checked.discrepancy !== 0 && (
                      <DiscrepancyBadge diff={checked.discrepancy} />
                    )}
                  </div>
                </div>
              ) : (
                /* ── Unchecked product — inline editing ───── */
                <div className="space-y-2">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-gray-100">
                        <div className="w-2 h-2 rounded-full bg-gray-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{sl.name}</p>
                        <p className="text-xs text-gray-500">
                          {sl.sku}
                          <span className="sm:hidden"> • Ожидается: {sl.quantity}</span>
                        </p>
                      </div>
                      <span className="hidden sm:inline text-xs text-gray-400 shrink-0">
                        Ожидается: {sl.quantity}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap pl-11 sm:pl-0">
                      <QuantityStepper
                        value={qty}
                        onChange={(v) => setQuantity(sl.product_id, v)}
                        disabled={isItemRecording}
                      />

                      {diff !== 0 && <DiscrepancyBadge diff={diff} />}

                      <button
                        type="button"
                        onClick={() => toggleNotes(sl.product_id)}
                        className={cn(
                          'flex items-center justify-center w-9 h-9 rounded-lg border transition-all duration-150',
                          noteExpanded
                            ? 'border-amber-300 bg-amber-50 text-amber-600'
                            : 'border-gray-200 bg-white text-gray-400 hover:bg-gray-50 hover:text-gray-600',
                        )}
                        aria-label="Примечание"
                      >
                        <StickyNote className="w-3.5 h-3.5" />
                      </button>

                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => handleRecordItem(sl.product_id)}
                        loading={isItemRecording}
                        disabled={isRecording && recordingId !== sl.product_id}
                        className="whitespace-nowrap"
                      >
                        {isItemRecording ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span className="sm:inline hidden">Запись...</span>
                          </>
                        ) : (
                          'Записать'
                        )}
                      </Button>
                    </div>
                  </div>

                  {noteExpanded && (
                    <div className="pl-11">
                      <input
                        type="text"
                        value={notes[sl.product_id] ?? ''}
                        onChange={(e) => setNote(sl.product_id, e.target.value)}
                        disabled={isItemRecording}
                        placeholder="Примечание..."
                        className={cn(
                          'w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg',
                          'focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400',
                          'placeholder-gray-400 disabled:bg-gray-50 disabled:cursor-not-allowed',
                        )}
                      />
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* ── Complete button ──────────────────────────────────── */}
      {allChecked && (
        <Button
          onClick={onComplete}
          loading={isCompleting}
          size="lg"
          className="w-full bg-emerald-600 hover:bg-emerald-700 focus-visible:ring-emerald-400"
        >
          <ClipboardCheck className="w-5 h-5" />
          Завершить проверку
        </Button>
      )}
    </div>
  );
};

export default CheckFlow;
